/**
 * useColumnManagement Hook
 * Manages column visibility, ordering, drag-drop, and resizing for dashboard tables
 * 
 * Supports two modes:
 * 1. Default mode: Uses /user/preferences API with storageKey
 * 2. Custom mode: Uses customLoadFn and customSaveFn for different API patterns
 *
 * iter 264d.18 — default-mode load path now uses the shared
 * mergeColumnsWithDefault helper so a user's drag-drop order survives when
 * new columns get added to DEFAULT_COLUMNS after they saved (the same
 * class of pref-drift bug as iter 264d.16's Mainline status-order case).
 */
import { useState, useCallback, useEffect } from 'react';
import { mergeColumnsWithDefault } from '../utils/mergePrefsWithDefaults';

export const useColumnManagement = ({
  defaultColumns,
  storageKey,
  api,
  onColumnsChange,
  customLoadFn,  // Optional: async () => columns[]
  customSaveFn,  // Optional: async (columns) => void
  protectedColumns = ['marker', 'actions'] // Columns that can't be dragged/resized
}) => {
  const [columns, setColumns] = useState(defaultColumns);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  const [savingColumns, setSavingColumns] = useState(false);
  const [columnsChanged, setColumnsChanged] = useState(false);

  // Load saved column preferences
  const loadColumnPreferences = useCallback(async () => {
    // Use custom load function if provided
    if (customLoadFn) {
      try {
        const savedCols = await customLoadFn();
        if (savedCols && savedCols.length > 0) {
          // Custom load function already handles merging and ordering
          // Just set the columns directly as returned
          setColumns(savedCols);
        }
      } catch (error) {
        console.error('Failed to load column preferences:', error);
      }
      return;
    }
    
    // Default API pattern
    if (!api || !storageKey) return;
    
    try {
      const response = await api.get('/user/preferences');
      const prefs = response.data;
      
      if (prefs?.column_preferences?.[storageKey]) {
        const savedCols = prefs.column_preferences[storageKey];
        // iter 264d.18 — merge preserves saved drag-drop order for known
        // columns and slots new defaults in at their default index.
        setColumns(mergeColumnsWithDefault(savedCols, defaultColumns));
      }
    } catch (error) {
      console.error('Failed to load column preferences:', error);
    }
  }, [api, storageKey, defaultColumns, customLoadFn]);

  // Save column preferences
  const saveColumnPreferences = useCallback(async (newColumns) => {
    // Update local state first if new columns provided
    if (newColumns) {
      setColumns(newColumns);
    }
    
    const columnsToSave = newColumns || columns;
    
    // Use custom save function if provided
    if (customSaveFn) {
      setSavingColumns(true);
      try {
        await customSaveFn(columnsToSave);
        setColumnsChanged(false);
      } catch (error) {
        console.error('Failed to save column preferences:', error);
      } finally {
        setSavingColumns(false);
      }
      return;
    }
    
    // Default API pattern
    if (!api || !storageKey) return;
    
    setSavingColumns(true);
    try {
      await api.put('/user/preferences', {
        column_preferences: {
          [storageKey]: columnsToSave
        }
      });
      setColumnsChanged(false);
    } catch (error) {
      console.error('Failed to save column preferences:', error);
    } finally {
      setSavingColumns(false);
    }
  }, [api, storageKey, columns, customSaveFn]);

  // Toggle column visibility
  const toggleColumnVisibility = useCallback((columnId) => {
    setColumns(prev => {
      const updated = prev.map(col =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      );
      setColumnsChanged(true);
      return updated;
    });
  }, []);

  // Update column width
  const updateColumnWidth = useCallback((columnId, width) => {
    setColumns(prev => {
      const updated = prev.map(col =>
        col.id === columnId ? { ...col, width: Math.max(30, width) } : col
      );
      setColumnsChanged(true);
      return updated;
    });
  }, []);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, columnId) => {
    // Don't allow dragging protected columns
    if (protectedColumns.includes(columnId)) {
      e.preventDefault();
      return;
    }
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  }, [protectedColumns]);

  const handleDragOver = useCallback((e, columnId) => {
    e.preventDefault();
    // Don't allow dropping on protected columns
    if (protectedColumns.includes(columnId)) return;
    if (draggedColumn && draggedColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  }, [draggedColumn, protectedColumns]);

  const handleDragEnd = useCallback(() => {
    if (draggedColumn && dragOverColumn && draggedColumn !== dragOverColumn) {
      // Double-check protected columns
      if (protectedColumns.includes(draggedColumn) || protectedColumns.includes(dragOverColumn)) {
        setDraggedColumn(null);
        setDragOverColumn(null);
        return;
      }
      setColumns(prev => {
        const newColumns = [...prev];
        const draggedIdx = newColumns.findIndex(c => c.id === draggedColumn);
        const overIdx = newColumns.findIndex(c => c.id === dragOverColumn);
        
        if (draggedIdx !== -1 && overIdx !== -1) {
          const [removed] = newColumns.splice(draggedIdx, 1);
          newColumns.splice(overIdx, 0, removed);
          setColumnsChanged(true);
        }
        return newColumns;
      });
    }
    setDraggedColumn(null);
    setDragOverColumn(null);
  }, [draggedColumn, dragOverColumn]);

  // Resize handlers
  const handleResizeStart = useCallback((e, columnId, currentWidth) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnId);
    setResizeStartX(e.clientX);
    setResizeStartWidth(currentWidth);
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!resizingColumn) return;
    
    const diff = e.clientX - resizeStartX;
    const newWidth = Math.max(30, resizeStartWidth + diff);
    updateColumnWidth(resizingColumn, newWidth);
  }, [resizingColumn, resizeStartX, resizeStartWidth, updateColumnWidth]);

  const handleResizeEnd = useCallback(() => {
    if (resizingColumn) {
      setResizingColumn(null);
      // Auto-save after resize
      if (columnsChanged) {
        saveColumnPreferences(columns);
      }
    }
  }, [resizingColumn, columnsChanged, columns, saveColumnPreferences]);

  // Set up resize event listeners
  useEffect(() => {
    if (resizingColumn) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizingColumn, handleResizeMove, handleResizeEnd]);

  // Notify parent of changes
  useEffect(() => {
    if (onColumnsChange) {
      onColumnsChange(columns);
    }
  }, [columns, onColumnsChange]);

  // Get visible columns
  const visibleColumns = columns.filter(col => col.visible);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setColumns(defaultColumns);
    setColumnsChanged(true);
  }, [defaultColumns]);

  return {
    columns,
    setColumns,
    visibleColumns,
    draggedColumn,
    dragOverColumn,
    resizingColumn,
    savingColumns,
    columnsChanged,
    loadColumnPreferences,
    saveColumnPreferences,
    toggleColumnVisibility,
    updateColumnWidth,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleResizeStart,
    resetToDefaults
  };
};

export default useColumnManagement;
